import React, { useEffect, useState } from 'react';
import {
  Award,
  BrainCircuit,
  Terminal as TerminalSquare,
} from 'lucide-react';

const FEATURE_TONES = {
  blue: {
    wrap: 'bg-brand/10 border-brand/30 shadow-[0_0_20px_rgba(0,82,206,0.2)] group-hover:bg-brand/20',
    icon: 'text-brand-accent',
    kicker: 'text-brand-accent',
  },
  emerald: {
    wrap: 'bg-brand-accent/10 border-brand-accent/20 shadow-[0_0_20px_rgba(66,165,245,0.18)] group-hover:bg-brand-accent/20',
    icon: 'text-brand-accent',
    kicker: 'text-brand-accent',
  },
  slate: {
    wrap: 'bg-slate-500/10 border-slate-500/20 shadow-[0_0_20px_rgba(148,163,184,0.1)] group-hover:bg-slate-500/20',
    icon: 'text-white',
    kicker: 'text-slate-400',
  },
};

export function FeatureCard({
  size = 'compact',
  tone = 'emerald',
  kicker,
  title,
  description,
  icon: Icon,
  className = '',
}) {
  const large = size === 'large';
  const palette = FEATURE_TONES[tone] || FEATURE_TONES.emerald;

  return (
    <div
      className={`${
        large
          ? 'h-full min-h-[400px] rounded-[2rem] premium-card p-10 md:p-14 flex flex-col justify-between group hover:-translate-y-2'
          : 'flex-1 rounded-[2rem] premium-card p-8 group hover:-translate-y-1'
      } transition-transform duration-500 ${className}`}
    >
      <div
        className={`${
          large ? 'w-16 h-16 rounded-2xl mb-10' : 'w-12 h-12 rounded-xl mb-6'
        } flex items-center justify-center border transition-colors ${palette.wrap}`}
      >
        <Icon className={large ? `w-8 h-8 ${palette.icon}` : `w-6 h-6 ${palette.icon}`} />
      </div>

      <div>
        <p className={`text-xs font-bold tracking-widest uppercase ${large ? 'mb-4' : 'mb-2'} ${palette.kicker}`}>
          {kicker}
        </p>
        <h3 className={`${large ? 'text-3xl md:text-4xl font-semibold mb-6' : 'text-xl font-semibold mb-3'} text-white`}>
          {title}
        </h3>
        <p className={`text-slate-300 leading-relaxed font-light ${large ? 'text-lg max-w-lg' : 'text-sm'}`}>
          {description}
        </p>
      </div>
    </div>
  );
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function useTypedText(fullText, active, { speed = 7, delay = 0 } = {}) {
  const [text, setText] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!active) {
      setText('');
      setDone(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setText(fullText);
      setDone(true);
      return undefined;
    }

    let i = 0;
    let intervalId;
    const startId = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        i += 1;
        setText(fullText.slice(0, i));
        if (i >= fullText.length) {
          window.clearInterval(intervalId);
          setDone(true);
        }
      }, speed);
    }, delay);

    return () => {
      window.clearTimeout(startId);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [active, delay, fullText, speed]);

  return [text, done];
}

function TypeBody({ text, phase, index }) {
  const typing = phase === 'in' && !prefersReducedMotion();
  const [typed, done] = useTypedText(text, typing, { speed: 7, delay: 90 + index * 160 });
  const shown = phase === 'pre' ? '' : phase === 'leave' || !typing ? text : typed;
  const showCaret = typing && !done && shown.length < text.length;

  return (
    <p className="cq-serve-desc mb-6 text-base font-light leading-relaxed text-slate-300">
      {shown}
      {showCaret ? <span className="cq-serve-caret" aria-hidden="true" /> : null}
    </p>
  );
}

export function AudienceCard({ title, subtitle, desc, deliverables, icon: Icon, phase, index = 0 }) {
  const animated = phase != null;

  return (
    <div
      className={
        animated
          ? `cq-serve-card is-${phase} group relative flex h-full min-h-[420px] flex-col justify-start rounded-[2rem] p-8 premium-card`
          : 'group relative flex h-full min-h-[420px] flex-col justify-start rounded-[2rem] p-8 premium-card transition-transform duration-500 hover:-translate-y-2'
      }
      style={animated ? { '--cq-i': index } : undefined}
      data-index={animated ? index : undefined}
    >
      <div className="relative z-10 flex h-full flex-col">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-brand-accent/80">{subtitle}</p>
            <h3 className="text-3xl font-semibold text-white">{title}</h3>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-3 transition-colors duration-500 group-hover:border-brand-accent/30 group-hover:bg-brand/10">
            <Icon className="h-6 w-6 text-slate-300 transition-colors duration-500 group-hover:text-brand-accent" />
          </div>
        </div>

        {animated ? (
          <TypeBody text={desc} phase={phase} index={index} />
        ) : (
          <p className="mb-6 text-base font-light leading-relaxed text-slate-300">{desc}</p>
        )}

        <div className="mt-auto border-t border-white/10 pt-6">
          <p className="mb-4 text-xs font-bold uppercase tracking-widest text-slate-500">Verified Outputs</p>
          <ul className="space-y-3">
            {deliverables.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm font-medium text-slate-200">
                <Award className="h-4 w-4 text-brand-accent/70" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function ProcessStep({ num, title, detail, outputs, active = false }) {
  return (
    <div className="flex flex-col md:flex-row items-stretch gap-6 md:gap-0 relative group">
      <div className="absolute left-6 md:left-[120px] -translate-x-[calc(50%-1px)] top-10 flex items-center justify-center">
        <div
          data-cq-dot
          className={`w-4 h-4 rounded-full border-2 z-10 transition-all duration-500 ${
            active
              ? 'bg-brand-accent border-brand-accent shadow-[0_0_16px_4px_rgba(66,165,245,0.45)]'
              : 'bg-[#060606] border-slate-500 group-hover:border-brand-accent group-hover:bg-brand-accent shadow-[0_0_0_0_rgba(66,165,245,0)] group-hover:shadow-[0_0_20px_4px_rgba(66,165,245,0.4)]'
          }`}
        ></div>
      </div>

      <div className="hidden md:flex w-[120px] pt-8 pr-12 justify-end">
        <span className={`text-4xl font-light font-serif italic transition-colors duration-500 ${
          active ? 'text-brand-accent' : 'text-slate-600 group-hover:text-brand-accent'
        }`}>
          {num}
        </span>
      </div>

      <div className="flex-1 ml-16 md:ml-12">
        <div className="premium-card p-8 md:p-10 rounded-[2rem] group-hover:-translate-y-1 transition-transform duration-500">
          <div className="md:hidden text-xl font-serif italic text-brand-accent mb-3">{num}</div>
          <h3 className="text-3xl font-semibold mb-4 text-white group-hover:text-white transition-colors duration-300">
            {title}
          </h3>
          <p className="text-slate-300 text-lg leading-relaxed mb-8 font-light">
            {detail}
          </p>
          <div className="flex flex-wrap gap-3">
            {outputs.map((output, i) => (
              <span key={i} className="px-4 py-2 text-xs font-bold tracking-widest text-slate-300 bg-white/5 rounded-full border border-white/10 uppercase group-hover:border-brand-accent/30 group-hover:text-brand-accent group-hover:bg-brand/10 transition-colors duration-500">
                {output}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TechModuleCard({
  code,
  title,
  description,
  tags,
  icon: Icon,
  iconWrapClass,
  iconClass,
}) {
  return (
    <div className="premium-card p-10 rounded-[2rem] h-full group hover:-translate-y-1 transition-transform duration-500">
      <div className="flex justify-between items-start mb-8">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${iconWrapClass}`}>
          <Icon className={`w-7 h-7 ${iconClass}`} />
        </div>
        <span className="text-[10px] font-mono tracking-widest text-slate-500 uppercase">{code}</span>
      </div>
      <h3 className="text-2xl font-semibold text-white mb-4">{title}</h3>
      <p className="text-slate-300 font-light leading-relaxed mb-6">
        {description}
      </p>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span key={tag} className="text-xs font-medium text-slate-400 bg-white/5 border border-white/10 px-3 py-1 rounded-full">
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

export function OrchestrationBanner() {
  return (
    <div className="premium-card p-10 md:p-12 rounded-[2rem] mb-12 flex flex-col md:flex-row items-center justify-between gap-10 border border-white/5 grain-noise bg-[length:100px] mix-blend-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-brand/10 to-brand-accent/10 opacity-50"></div>

      <div className="relative z-10 flex-1">
        <div className="flex items-center gap-3 mb-4">
          <BrainCircuit className="w-8 h-8 text-brand-accent" />
          <h3 className="text-3xl font-semibold text-white">Agentic Orchestration</h3>
        </div>
        <p className="text-slate-300 text-lg font-light leading-relaxed mb-6">
          Our core framework harnesses distinct AI agents, strictly controlling their workflows. We force the system to explain its reasoning, document its steps, and benchmark its extractions against validated clinical ground-truths before a human ever sees the draft.
        </p>
        <div className="flex gap-4">
          <span className="px-3 py-1.5 text-[10px] font-mono tracking-widest text-brand-accent border border-brand-accent/30 bg-brand/10 uppercase rounded">Step-by-Step Verification</span>
          <span className="px-3 py-1.5 text-[10px] font-mono tracking-widest text-brand-accent border border-brand-accent/30 bg-brand/10 uppercase rounded">Benchmarked Accuracy</span>
        </div>
      </div>

      <div className="relative z-10 hidden md:flex items-center justify-center w-64 h-64">
        <div className="relative w-full h-full flex items-center justify-center">
          <div className="absolute inset-0 border border-brand-accent/20 rounded-full animate-[spin_20s_linear_infinite]"></div>
          <div className="absolute inset-4 border border-brand/20 rounded-full animate-[spin_15s_linear_infinite_reverse]"></div>
          <div className="absolute inset-12 border border-slate-500/20 rounded-full animate-[spin_10s_linear_infinite]"></div>
          <TerminalSquare className="w-12 h-12 text-slate-300 relative z-10" />
        </div>
      </div>
    </div>
  );
}

export function DivisionCard({ name }) {
  return (
    <div className="premium-card p-8 rounded-[2rem] flex flex-col justify-center h-40 group hover:border-brand-accent/30 transition-colors">
      <p className="text-xs font-bold text-brand-accent/70 tracking-widest uppercase mb-2">Division</p>
      <h3 className="text-xl font-semibold text-white group-hover:text-white transition-colors">{name}</h3>
    </div>
  );
}

export function CaseCard({ title, type, metric, desc }) {
  return (
    <div className="premium-card p-8 md:p-10 rounded-[2rem] flex flex-col md:flex-row gap-8 justify-between items-center group">
      <div className="flex-1">
        <p className="text-xs font-bold text-brand-accent tracking-widest uppercase mb-3">{type}</p>
        <h3 className="text-3xl font-semibold text-white mb-4">{title}</h3>
        <p className="text-slate-300 font-light leading-relaxed max-w-2xl">{desc}</p>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-[1.5rem] p-6 text-center min-w-[220px] group-hover:border-brand-accent/30 group-hover:bg-brand/10 transition-colors duration-500">
        <h4 className="text-2xl font-bold text-white group-hover:text-brand-accent transition-colors mb-1">{metric}</h4>
        <span className="text-xs font-medium text-slate-500 uppercase tracking-widest">Key Outcome</span>
      </div>
    </div>
  );
}

export function PageEyebrow({ icon: Icon, children, tone = 'emerald' }) {
  const tones = {
    emerald: 'border-brand-accent/30 bg-brand/10 text-brand-accent',
    blue: 'border-brand/30 bg-brand/10 text-brand-accent',
    slate: 'border-white/10 bg-white/5 text-slate-300',
  };

  return (
    <div className={`inline-flex items-center gap-2 mb-8 px-4 py-1.5 rounded-full border text-xs font-semibold tracking-widest uppercase ${tones[tone]}`}>
      <Icon className="w-4 h-4" />
      {children}
    </div>
  );
}
